import React, { type ReactNode } from 'react';
import type { UseInfinitePaginationReturn } from '@hero/api-hooks';
import Spinner from '../Spinner/Spinner';
import { InfiniteScrollSentinel } from '../Table/InfiniteScrollSentinel';
import { cn } from '@hero/web-lib';

export interface SharedListProps<T> {
  /** The infinite query result from useInfiniteQueryAPI or useInfinitePagination */
  query: Pick<
    UseInfinitePaginationReturn<T>,
    'data' | 'isLoading' | 'onLoadMore' | 'hasNextPage' | 'isFetchingNextPage'
  >;
  /** Render function for each item */
  renderItem: (item: T, index: number) => ReactNode;
  /** Message shown when the list is empty after loading */
  emptyMessage?: string;
  /** Additional class for the list container */
  className?: string;
  /** Extract a unique key for each item (defaults to item._id or index) */
  getItemId?: (item: T, index: number) => string;
  /**
   * Container layout. `'list'` (default) stacks one card per row
   * (`flex flex-col`). `'grid'` lays cards out responsively — single
   * column on mobile, two columns from `md` upwards — useful for
   * compact card content (e.g. contacts) where stacking wastes
   * horizontal space and forces excessive scrolling once the list
   * grows past ~10 items.
   */
  layout?: 'list' | 'grid';
}

function defaultGetItemId<T>(item: T, index: number): string {
  const id = (item as { _id?: { toString?: () => string } | string })._id;
  if (id) return typeof id === 'string' ? id : id.toString?.() || String(index);
  return String(index);
}

/**
 * SharedList — reusable infinite-scroll list for card/item layouts.
 *
 * Accepts a `query` object (from useInfiniteQueryAPI / useInfinitePagination)
 * and a `renderItem` callback. Handles loading, empty, and pagination states internally.
 */
export function SharedList<T>({
  query,
  renderItem,
  emptyMessage = 'No items found',
  className,
  getItemId,
  layout = 'list',
}: SharedListProps<T>) {
  const { data, isLoading, onLoadMore, hasNextPage, isFetchingNextPage } = query;
  const getId = getItemId ?? defaultGetItemId;

  if (isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  const sentinel = (
    <InfiniteScrollSentinel
      onVisible={onLoadMore}
      active={hasNextPage && !isFetchingNextPage}
      loading={isFetchingNextPage}
      showEnd={!hasNextPage && data.length > 0}
    />
  );

  // In `'grid'` mode the sentinel must escape the grid track so it
  // doesn't become a single half-width grid cell next to the last
  // card — the sentinel is a full-width "loading more / end of list"
  // affordance that should always span the row beneath all cards.
  if (layout === 'grid') {
    return (
      <>
        <div className={cn('grid grid-cols-1 md:grid-cols-2', className)}>
          {data.map((item, index) => (
            <React.Fragment key={getId(item, index)}>
              {renderItem(item, index)}
            </React.Fragment>
          ))}
        </div>
        {sentinel}
      </>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {data.map((item, index) => (
        <React.Fragment key={getId(item, index)}>
          {renderItem(item, index)}
        </React.Fragment>
      ))}
      {sentinel}
    </div>
  );
}
